import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Error boundary (U-1, PRD NFR-01).
 *
 * Before this, one crash in any component, or a lazy route chunk that 404s after a deploy (nginx no
 * longer has the old hashed file), left a blank white page. Now:
 *  - a chunk-load failure reloads the page once automatically (the new deploy's index.html knows the
 *    new chunk names); a second one in the same session shows the recovery screen, so a genuinely
 *    missing chunk can never loop;
 *  - any other render error shows the recovery screen with a Reload button.
 * sessionStorage can throw (private mode, blocked storage); when it does we cannot prove we have not
 * already reloaded, so we do not auto-reload.
 */

export const RELOAD_GUARD_KEY = 'foodops.chunkReloadAttempted';

export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = String(error.message || error);
  return /Loading (CSS )?chunk \S+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /error loading dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message);
}

function readGuard() {
  try { return !!window.sessionStorage.getItem(RELOAD_GUARD_KEY); } catch (e) { return true; }
}
function setGuard() {
  try { window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now())); return true; } catch (e) { return false; }
}
function clearGuard() {
  try { window.sessionStorage.removeItem(RELOAD_GUARD_KEY); } catch (e) { /* storage unavailable */ }
}

const defaultReload = () => window.location.reload();

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, gaveUp: false, resetKey: props.resetKey };
    this.onReload = this.onReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error, gaveUp: false };
  }

  static getDerivedStateFromProps(props, state) {
    // Route-level use: leaving the route that crashed clears the error.
    if (props.resetKey !== state.resetKey) {
      return { resetKey: props.resetKey, error: null, gaveUp: false };
    }
    return null;
  }

  componentDidMount() {
    // A page that has stayed up is a healthy deploy: allow a later deploy in this session its own auto-reload.
    this.healthyTimer = setTimeout(clearGuard, 15000);
  }

  componentWillUnmount() {
    clearTimeout(this.healthyTimer);
  }

  componentDidCatch(error, info) {
    // No front-end error reporter exists yet; the console is what an operator's DevTools shows.
    console.error('ErrorBoundary caught', error, info && info.componentStack);
    if (isChunkLoadError(error) && !readGuard() && setGuard()) {
      (this.props.reload || defaultReload)();
      return;
    }
    this.setState({ gaveUp: true });
  }

  onReload() {
    clearGuard();
    (this.props.reload || defaultReload)();
  }

  render() {
    const { error, gaveUp } = this.state;
    if (!error) return this.props.children;
    if (isChunkLoadError(error) && !gaveUp) {
      return (
        <div role="status" className="min-h-[40vh] flex items-center justify-center p-6 text-slate-600 text-sm" data-testid="error-boundary-reloading">
          Updating to the latest version…
        </div>
      );
    }
    return (
      <div className="min-h-[60vh] bg-slate-50 flex items-center justify-center p-6" role="alert" data-testid="error-boundary">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 text-center">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-slate-600 text-sm mb-6">
            This page could not be shown. Reloading usually fixes it, especially just after an update.
            If it keeps happening, tell your administrator.
          </p>
          <button
            type="button"
            onClick={this.onReload}
            className="inline-block px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}

/** Boundary for the routed area: resets when the route changes, so navigating away from a crashed page works. */
export function RouteErrorBoundary({ children, reload }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname} reload={reload}>{children}</ErrorBoundary>;
}
